import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DOCUMENT, Inject, Input, OnDestroy, OnInit } from '@angular/core';

import { FormGroupDirective, NgForm, UntypedFormControl, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ServiceItem } from '@shared/serviceitem';
import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import { catchError, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FederatedInfoResult } from '../../shared/federatedinforesult';
import { LocalStorageService, SessionStorageService } from '../../shared/services/storage.service';
import { serviceItems } from '../../topbar/servicemenu';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
	isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
		const isSubmitted = form && form.submitted;
		return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
	}
}

@Component({
	selector: 'app-sign-in',
	templateUrl: './sign-in.component.html',
	styleUrls: ['./sign-in.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SignInComponent implements OnInit, OnDestroy {
	isLoading: boolean = false;
	show: boolean = false;
	email_address: string = '';
	password: string = '';
	buttonText: string = '';
	hidePass: boolean = true;
	showError: boolean = false;
	rememberMe: boolean = false;
	buttonTranslateString: string = '';
	hasInvite: boolean = false;

	emailFormControl = new UntypedFormControl('', [Validators.email]);
	passwordFormControl = new UntypedFormControl('');
	/** The password error is a failed sign-in, not a validation error, so the field shows its error state while `showError` is set. */
	passwordErrorMatcher: ErrorStateMatcher = { isErrorState: () => this.showError };
	unsubscribe: Subject<void> = new Subject<void>();
	services: ServiceItem[] = serviceItems;

	@Input() error!: string | null;

	constructor(
		private router: Router,
		@Inject(DOCUMENT) private document: Document,
		private sessionStorageService: SessionStorageService,
		public http: HttpClient,
		private translate: TranslateService,
		public dialog: MatDialog,
		private route: ActivatedRoute,
		private localStorageService: LocalStorageService,
	) {}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		// Remove invite token if we're not coming to the signin page from the invite flow
		this.route.queryParams.subscribe((params) => {
			if (!('invited' in params)) {
				this.sessionStorageService.remove('User.InviteToken');
			}
		});

		const tempUser = this.localStorageService.get('User.Email');
		let tempRefresh;
		let tempClientId;
		let tempAdLogin = 'false';
		let tempIAMId;
		const remember = this.localStorageService.get('User.RememberMe');
		const tempLanguage = this.sessionStorageService.get('User.PreferredLanguage');

		if (remember === 'true' && tempUser != null && tempUser.length > 4) {
			console.log('remember me active....', tempUser);
			this.rememberMe = true;
			this.emailFormControl.setValue(tempUser);
			tempRefresh = this.localStorageService.get('User.RefreshToken');
			tempClientId = this.localStorageService.get('User.ClientId');
			tempAdLogin = this.localStorageService.get('AdLogin');
			tempIAMId = this.localStorageService.get('User.IAM.Id');
		}

		const tempToken = this.sessionStorageService.get('User.InviteToken');

		const tempAccount = this.sessionStorageService.get('User.DeepLink.Account');

		const tempNavigationDeepLink = this.sessionStorageService.get('Navigation.DeepLink');

		this.sessionStorageService.clear();

		if (tempAccount) {
			this.sessionStorageService.set('User.DeepLink.Account', tempAccount);
		}

		if (tempLanguage) {
			this.sessionStorageService.set('User.PreferredLanguage', tempLanguage);
			this.localStorageService.set('User.PreferredLanguage', tempLanguage);
		}

		if (tempToken) {
			this.sessionStorageService.set('User.InviteToken', tempToken);
			this.hasInvite = true;
		}

		if (tempNavigationDeepLink) {
			this.sessionStorageService.set('Navigation.DeepLink', tempNavigationDeepLink);
		}

		if (this.rememberMe) {
			this.sessionStorageService.set('User.RememberMe', true);
			this.sessionStorageService.set('User.Email', tempUser);
			this.localStorageService.set('User.RememberMe', true);
			this.localStorageService.set('User.Email', tempUser);

			if (tempRefresh && tempRefresh !== 'null') {
				this.sessionStorageService.set('User.RefreshToken', tempRefresh);
				this.localStorageService.set('User.RefreshToken', tempRefresh);
			}

			if (tempClientId && tempClientId !== 'null') {
				this.sessionStorageService.set('User.ClientId', tempClientId);
				this.localStorageService.set('User.ClientId', tempClientId);
			}

			if (tempAdLogin && tempAdLogin !== 'null') {
				this.sessionStorageService.set('AdLogin', tempAdLogin);
				this.localStorageService.set('AdLogin', tempAdLogin);
			}

			if (tempIAMId && tempIAMId !== 'null') {
				this.sessionStorageService.set('User.IAM.Id', tempIAMId);
				this.localStorageService.set('User.IAM.Id', tempIAMId);
			}

			if (tempRefresh && tempRefresh !== 'null') {
				this.handleRememberMeLogin();
			}
		} else {
			this.localStorageService.remove('User.Selected.ServiceName');
			this.localStorageService.remove('User.Selected.Account');
		}

		this.buttonTranslateString = 'login.btnNext.label';
	}

	private handleRememberMeLogin() {
		console.log('handleRememberMeLogin');

		const poolData = {
			Storage: window.sessionStorage,
			UserPoolId: environment.cognitoUserPoolId,
			ClientId: this.sessionStorageService.get('User.ClientId') ? this.sessionStorageService.get('User.ClientId') : environment.cognitoAppClientId,
		};

		const userPool = new CognitoUserPool(poolData);
		const userData = {
			Username: this.sessionStorageService.get('User.Email'),
			Pool: userPool,
			Storage: window.sessionStorage,
		};
		const cognitoUser = new CognitoUser(userData);
		let refreshToken: CognitoRefreshToken = null;
		refreshToken = new CognitoRefreshToken({
			RefreshToken: this.localStorageService.get('User.RefreshToken'),
		});
		cognitoUser.refreshSession(refreshToken, (err: any, session: any) => {
			this.sessionStorageService.set('User.Email', this.localStorageService.get('User.Email'));
			this.sessionStorageService.set('User.PreferredLanguage', this.localStorageService.get('User.PreferredLanguage'));
			this.sessionStorageService.set('User.IAM.Id', this.localStorageService.get('User.IAM.Id'));
			this.sessionStorageService.set('User.RememberMe', this.localStorageService.get('User.RememberMe'));
			console.log(`email: ${this.sessionStorageService.get('User.Email')}`);
			if (err) {
				// alert(err.message || JSON.stringify(err));
				this.isLoading = false;
				return;
			}
			const identityjwtdecoded: any = jwtDecode(session.getIdToken().getJwtToken());
			this.sessionStorageService.set('User.Session.Expire', identityjwtdecoded.exp);
			this.sessionStorageService.set('User.Authorization', session.getAccessToken().getJwtToken());
			this.sessionStorageService.set('User.RefreshToken', session.getRefreshToken().getToken());
			this.localStorageService.set('User.RefreshToken', session.getRefreshToken().getToken());
			this.isLoading = false;
			if (this.localStorageService.getItem('User.DeepLink')?.length > 0) {
				const location = this.localStorageService.getItem('User.DeepLink').split('?')[0];
				void this.router.navigate([location.startsWith('/') ? location.substring(1) : location]);
			} else {
				void this.router.navigate(['core']);
			}
		});
	}

	onReturn() {
		this.isLoading = false;
		this.show = !this.show;
		if (this.showError) {
			this.showError = !this.showError;
		}
		this.buttonTranslateString = 'login.btnNext.label';
		this.passwordFormControl.setValue('');
		this.passwordFormControl.markAsUntouched();
		setTimeout(() => {
			(document.getElementById('emailInput') as HTMLInputElement).select();
		}, 100);
	}

	onSignIn() {
		this.emailFormControl.markAllAsTouched();
		if (this.emailFormControl.valid && this.emailFormControl.value) {
			if (!this.show) {
				this.isLoading = true;

				let domain = '';
				const email = this.emailFormControl.value;
				domain = email.split('@', 2)[1];
				const prefix = email.split('@', 2)[0];
				const plus = prefix.split('+');
				if (plus.length !== 2 || (plus.length === 2 && plus[1] === '')) {
					this.getFederatedInfoAndHandleLogin(domain);
				} else {
					this.handleStandardLogin();
				}
			} else {
				this.handleStandardLogin();
			}
		}
	}

	onADLogin(providerName: string, appClientId: string): void {
		this.document.location.href = `${environment.oAuthUri}/oauth2/authorize?identity_provider=${providerName}&redirect_uri=${window.location.origin}${environment.redirectUri}&response_type=CODE&client_id=${appClientId}&scope=aciescore/apis%20openid`;
	}

	onChangeRemember(ob: MatCheckboxChange) {
		if (!ob.checked) {
			this.sessionStorageService.remove('User.RememberMe');
			this.sessionStorageService.remove('User.Email');
			this.localStorageService.remove('User.RememberMe');
			this.localStorageService.remove('User.Email');
			this.localStorageService.remove('User.Selected.ServiceName');
			this.localStorageService.remove('User.Selected.Account');
		} else {
			this.sessionStorageService.set('User.RememberMe', true);
			this.localStorageService.set('User.RememberMe', true);
		}
	}

	getFederatedInfoAndHandleLogin(domain: string): any {
		const federatedDomainURL = `${environment.awsEnvUri}/iam/federateddomainsinfo/${domain}`;
		this.http
			.get<any>(federatedDomainURL)
			.pipe(
				takeUntil(this.unsubscribe),
				catchError(() => this.handleStandardLogin()),
			)
			.subscribe((data) => this.handleFederatedLogin(data));
	}

	handleFederatedLogin(data: FederatedInfoResult) {
		this.isLoading = false;
		console.log('provider', data.result.providerName);
		this.sessionStorageService.set('User.AzureClientId', data.result.azureClientId);
		this.sessionStorageService.set('User.ClientId', data.result.appClientId);
		this.onADLogin(data.result.providerName, data.result.appClientId);
	}

	onKeyDown(event) {
		event.preventDefault();

		if (event.clientX > 0) {
			this.hidePass = !this.hidePass;
		} else {
			this.onSignIn();
		}
	}

	handleStandardLogin(): [] {
		this.isLoading = false;
		if (this.show === false) {
			this.show = true;
			this.buttonTranslateString = 'login.btnLogin.label';
			this.email_address = this.emailFormControl.value;
			setTimeout(() => {
				document.getElementById('passwordInput').focus();
			}, 100);
			return [];
		}

		this.passwordFormControl.markAllAsTouched();

		if (this.passwordFormControl.valid) {
			const authenticationDetails = new AuthenticationDetails({
				Username: this.emailFormControl.value,
				Password: this.passwordFormControl.value,
			});

			const poolData = {
				Storage: window.sessionStorage,
				UserPoolId: environment.cognitoUserPoolId,
				ClientId: this.sessionStorageService.get('User.ClientId') ? this.sessionStorageService.get('User.ClientId') : environment.cognitoAppClientId,
			};

			const userPool = new CognitoUserPool(poolData);

			const userData = {
				Username: this.emailFormControl.value,
				Pool: userPool,
				Storage: window.sessionStorage,
			};
			const cognitoUser = new CognitoUser(userData);

			this.isLoading = true;
			cognitoUser.authenticateUser(authenticationDetails, {
				onSuccess: () => {
					this.sessionStorageService.set('AdLogin', 'false');
					const idToken = cognitoUser.getSignInUserSession().getIdToken();
					const identityjwtdecoded: any = jwtDecode(idToken.getJwtToken());
					const email = identityjwtdecoded.email;
					if (this.localStorageService.get('User.Email') !== email) {
						this.localStorageService.removeItem('User.Selected.ServiceName');
						this.localStorageService.removeItem('User.Selected.Account');
					}
					this.sessionStorageService.set('User.Email', email);
					this.localStorageService.set('User.Email', email);

					this.sessionStorageService.set('User.Authorization', cognitoUser.getSignInUserSession().getAccessToken().getJwtToken());
					this.sessionStorageService.set('User.RefreshToken', cognitoUser.getSignInUserSession().getRefreshToken().getToken());
					this.sessionStorageService.set('User.Session.Expire', identityjwtdecoded.exp);

					if (this.rememberMe) {
						this.localStorageService.set('User.RefreshToken', cognitoUser.getSignInUserSession().getRefreshToken().getToken());
					}

					const deepLink = localStorage.getItem('User.DeepLink');
					if (deepLink?.length > 0) {
						void this.router.navigate([deepLink.startsWith('/') ? deepLink.substring(1) : deepLink]);
					} else {
						if (this.sessionStorageService.get('User.InviteToken')) {
							void this.router.navigate(['invite']);
						} else {
							void this.router.navigate(['core']);
						}
					}
				},
				onFailure: () => {
					this.showError = true;
					this.isLoading = false;
				},
			});
		}
		return [];
	}

	handleForgotPassword() {
		console.log('handle forgot password...');
		if (this.passwordFormControl.valid) {
			const poolData = {
				Storage: window.sessionStorage,
				UserPoolId: environment.cognitoUserPoolId,
				ClientId: this.sessionStorageService.get('User.ClientId') ? this.sessionStorageService.get('User.ClientId') : environment.cognitoAppClientId,
			};

			const userPool = new CognitoUserPool(poolData);
			const userData = {
				Username: this.emailFormControl.value,
				Pool: userPool,
			};
			const cognitoUser = new CognitoUser(userData);

			cognitoUser.forgotPassword({
				onSuccess: () => {
					this.dialog.open(DialogConfirmForgotPasswordComponent, {
						width: '300px',
						data: this.emailFormControl.value,
					});
				},
				onFailure: (err) => {
					console.log(`Error calling forgot password ${err.message}`);
				},
			});
		}
	}
}

@Component({
	selector: 'app-dialog-confirm-forgot-password',
	templateUrl: 'dialog-confirm-forgot-password.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogConfirmForgotPasswordComponent {
	confirmInput;
	newPasswordInput;
	userName: string;

	constructor(
		public dialogRef: MatDialogRef<DialogConfirmForgotPasswordComponent>,
		@Inject(MAT_DIALOG_DATA) public data: string,
		private sessionStorageService: SessionStorageService,
	) {
		this.userName = this.data;
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	onOkClick(): void {
		const poolData = {
			Storage: window.sessionStorage,
			UserPoolId: environment.cognitoUserPoolId,
			ClientId: this.sessionStorageService.get('User.ClientId') ? this.sessionStorageService.get('User.ClientId') : environment.cognitoAppClientId,
		};

		const userPool = new CognitoUserPool(poolData);
		const userData = {
			Username: this.userName,
			Pool: userPool,
			Storage: window.sessionStorage,
		};
		const cognitoUser = new CognitoUser(userData);

		cognitoUser.confirmPassword(this.confirmInput, this.newPasswordInput, {
			onSuccess: () => {
				console.log('Sending confirm code to aws cognito');
				this.dialogRef.close();
			},
			onFailure: (err) => {
				console.log(`Error calling confirm forgot password ${err.message}`);
			},
		});
	}
}
